import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import faqService from "../../../services/faqService";
import { instanceToken } from "../../../utils/constant";
import {
  EditPageLayout,
  EditFieldLabel,
} from "../../../components/edit/EditPageLayout";

const categoriesValue = {
  types: "အမျိုးအစားများ",
  basic: "အခြေခံ",
  other: "အခြား",
};

export default function UpdateFaq({ homeAlert }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [questionMM, setQuestionMM] = useState("");
  const [answers, setAnswers] = useState([{ answer_en: "", answer_mm: "" }]);
  const [category, setCategory] = useState("");
  const [categoryMM, setCategoryMM] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    async function fetchFaq() {
      const response = await faqService.getAllFaq(instanceToken.token, id);
      setAnswers(response?.data?.answers || [{ answer_en: "", answer_mm: "" }]);
      setCategory(response?.data?.category_en || "");
      setCategoryMM(response?.data?.category_mm || "");
      setQuestion(response?.data?.question_en || "");
      setQuestionMM(response?.data?.question_mm || "");
    }
    fetchFaq();
  }, [id]);

  const handleAddAnswer = () => {
    setAnswers([...answers, { answer_en: "", answer_mm: "" }]);
  };

  const handleAnswerChange = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;
    setAnswers(updatedAnswers);
  };

  const handleRemoveAnswer = (index) => {
    const updatedAnswers = [...answers];
    updatedAnswers.splice(index, 1);
    setAnswers(updatedAnswers);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const err = {};

    if (!question.trim()) err.question = "Question is required";
    if (
      answers.some(
        (answer) => !answer.answer_en.trim() || !answer.answer_mm.trim()
      )
    ) {
      err.answers = "All answer fields must be filled";
    }

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    try {
      setLoading(true);
      await faqService.updateFaq(instanceToken.token, id, {
        question_mm: questionMM,
        question_en: question,
        category_en: category,
        category_mm: categoryMM,
        answers,
      });
      homeAlert("FAQ has been updated.", false);
      navigate("/faqs");
    } catch (error) {
      homeAlert("Failed to update FAQ!", true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <EditPageLayout
      title="Edit FAQ"
      subtitle="Update frequently asked question details."
      breadcrumbs={[
        { label: "FAQs", path: "/faqs" },
        { label: `FAQ (ID - ${id})`, path: `/faqs/${id}` },
        { label: "Edit" },
      ]}
      detailPath={`/faqs/${id}`}
      listPath="/faqs"
      loading={loading}
      submitLabel="Update"
      onSubmit={handleUpdate}
    >
      <div className="edit-form-row-2">
        <FormControl>
          <EditFieldLabel>Category (English)</EditFieldLabel>
          <Select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setCategoryMM(categoriesValue[e.target.value] || "");
              setErrors({});
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>
            <MenuItem value="types">Types</MenuItem>
            <MenuItem value="basic">Basic</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <FormControl>
          <EditFieldLabel>Category (Myanmar)</EditFieldLabel>
          <Select
            value={categoryMM}
            onChange={(e) => {
              setCategoryMM(e.target.value);
              const selectedCategory = Object.keys(categoriesValue).find(
                (key) => categoriesValue[key] === e.target.value
              );
              setCategory(selectedCategory || "");
              setErrors({});
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>
            {Object.values(categoriesValue).map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="edit-form-row-2">
        <div>
          <EditFieldLabel required>Question (English)</EditFieldLabel>
          <TextField
            fullWidth
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            error={!!errors.question}
            helperText={errors.question}
          />
        </div>
        <div>
          <EditFieldLabel>Question (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            value={questionMM}
            onChange={(e) => setQuestionMM(e.target.value)}
            error={!!errors.questionMM}
            helperText={errors.questionMM}
          />
        </div>
      </div>

      <div className="edit-form-answers">
        <div className="edit-form-answers-head">
          <h3 className="edit-form-answers-title">Answers</h3>
          <button
            type="button"
            className="edit-btn edit-btn-back"
            onClick={handleAddAnswer}
          >
            + Add Answer
          </button>
        </div>
        {errors.answers && (
          <p className="edit-field-error">{errors.answers}</p>
        )}
        {answers.map((answer, index) => (
          <div key={index} className="edit-form-answer-row">
            <div>
              <EditFieldLabel required>Answer EN {index + 1}</EditFieldLabel>
              <TextField
                fullWidth
                value={answer.answer_en}
                onChange={(e) =>
                  handleAnswerChange(index, "answer_en", e.target.value)
                }
                error={!!errors.answers}
              />
            </div>
            <div>
              <EditFieldLabel required>Answer MM {index + 1}</EditFieldLabel>
              <TextField
                fullWidth
                value={answer.answer_mm}
                onChange={(e) =>
                  handleAnswerChange(index, "answer_mm", e.target.value)
                }
                error={!!errors.answers}
              />
            </div>
            {answers.length > 1 && (
              <button
                type="button"
                className="edit-btn edit-btn-remove-outline"
                onClick={() => handleRemoveAnswer(index)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </EditPageLayout>
  );
}
